// ============================================================
// rankings.js — o ranking da federação, ano a ano
//
// O ranking não sai dos resultados lançados aqui: o programa só enxerga
// os próprios atletas, e a federação ranqueia o estado inteiro. Por isso
// é digitado.
//
// Era UM par de números por atleta, o estadual e o nacional da última
// consulta, mais a data. Isso responde "onde ele está" e não responde "ele
// subiu?", que é a pergunta que a coordenação faz. Agora é uma linha por
// ANO, e a evolução sai da comparação entre anos consecutivos.
//
// ── Posição menor é melhor ──────────────────────────────────
//
// É o que torna a conta da evolução diferente de toda outra conta de
// percentual do sistema. Sair de 5º para 2º é MELHORAR, e o número cai.
// Por isso a variação é `(anterior - atual) / anterior`, e o sinal
// positivo quer dizer que o atleta subiu no ranking.
//
// Quem desenha precisa saber disso: verde é positivo aqui também, mas o
// número que gerou o verde é uma queda.
//
// ── "Sem ranking" não é vazio ───────────────────────────────
//
// `sem` diz que a federação não ranqueia aquele atleta naquele ano, e
// vazio diz que ninguém consultou. São fatos diferentes, e nenhum dos
// dois é posição: nenhum entra na conta de evolução.
// ============================================================

export const SEM_RANKING = "sem";

// A posição escrita, quando é posição.
//
// Aceita "2", "2º", " 2 ". Devolve null para "sem", para vazio e para o
// que não for número, porque nada disso dá o que comparar.
export function posicaoDoRanking(valor) {
  const texto = String(valor === undefined || valor === null ? "" : valor).trim();
  if (!texto) return null;
  if (texto.toLowerCase() === SEM_RANKING) return null;
  const n = Number(texto.replace(/[ºo°.\s]/gi, "").replace(",", "."));
  return Number.isFinite(n) && n >= 1 ? Math.round(n) : null;
}

export function rotuloDoRanking(valor) {
  const texto = String(valor === undefined || valor === null ? "" : valor).trim();
  if (!texto) return "";
  if (texto.toLowerCase() === SEM_RANKING) return "Sem ranking";
  const posicao = posicaoDoRanking(texto);
  return posicao === null ? texto : `${posicao}º`;
}

// O histórico em ordem de ano, do mais antigo ao mais novo.
//
// Ano repetido fica com a última ocorrência: duas linhas do mesmo ano é
// engano de digitação, e manter as duas faria a evolução comparar o ano
// com ele mesmo.
export function historicoOrdenado(rankings) {
  const porAno = new Map();
  (Array.isArray(rankings) ? rankings : []).forEach((r) => {
    const ano = Number(r && r.ano);
    if (!Number.isFinite(ano) || ano < 1900 || ano > 2200) return;
    porAno.set(ano, {
      ano,
      estadual: String((r && r.estadual) || "").trim(),
      nacional: String((r && r.nacional) || "").trim(),
    });
  });
  return [...porAno.values()].sort((a, b) => a.ano - b.ano);
}

// A linha mais recente que tem alguma coisa escrita.
export function rankingMaisRecente(rankings) {
  const historico = historicoOrdenado(rankings);
  for (let i = historico.length - 1; i >= 0; i -= 1) {
    if (historico[i].estadual || historico[i].nacional) return historico[i];
  }
  return null;
}

// Quanto o atleta andou de um ano para o outro, em posições e em
// percentual.
//
// Devolve null quando não há o que comparar: ano sem posição, ano anterior
// sem posição, ou "sem ranking" em qualquer dos dois. Null é diferente de
// zero, e a tela precisa poder dizer "não dá para comparar" em vez de
// "não mudou".
export function evolucaoEntre(anterior, atual) {
  const de = posicaoDoRanking(anterior);
  const para = posicaoDoRanking(atual);
  if (de === null || para === null) return null;
  return {
    posicoes: de - para,                       // positivo: subiu no ranking
    percentual: ((de - para) / de) * 100,
    de,
    para,
  };
}

// O histórico com a evolução de cada ano em relação ao ano anterior do
// MESMO histórico, e não do ano calendário anterior: se não houve consulta
// em 2025, a comparação de 2026 é com 2024, que é o último dado que
// existe. Pular o ano vazio é o que permite ler a série de quem só é
// consultado de dois em dois anos.
export function historicoComEvolucao(rankings) {
  const historico = historicoOrdenado(rankings);
  let ultimoEstadual = null;
  let ultimoNacional = null;

  return historico.map((linha) => {
    const evolucaoEstadual = evolucaoEntre(ultimoEstadual, linha.estadual);
    const evolucaoNacional = evolucaoEntre(ultimoNacional, linha.nacional);
    if (posicaoDoRanking(linha.estadual) !== null) ultimoEstadual = linha.estadual;
    if (posicaoDoRanking(linha.nacional) !== null) ultimoNacional = linha.nacional;
    return { ...linha, evolucaoEstadual, evolucaoNacional };
  });
}

// O percentual escrito como se lê, com o sinal à frente.
export function rotuloDaEvolucao(evolucao) {
  if (!evolucao) return "";
  const n = Math.round(evolucao.percentual * 10) / 10;
  const sinal = n > 0 ? "+" : "";
  return `${sinal}${String(n).replace(".", ",")}%`;
}

// Quantas posições, dito em palavra. É o que explica o percentual: 60% de
// evolução é "subiu 3 posições, de 5º para 2º", e o número sozinho não diz
// isso a ninguém.
export function rotuloDasPosicoes(evolucao) {
  if (!evolucao) return "";
  const n = Math.abs(evolucao.posicoes);
  if (n === 0) return `manteve o ${evolucao.de}º`;
  const verbo = evolucao.posicoes > 0 ? "subiu" : "caiu";
  return `${verbo} ${n} ${n > 1 ? "posições" : "posição"}, de ${evolucao.de}º para ${evolucao.para}º`;
}

// Converte o formato antigo, de um par de números mais a data, na primeira
// linha do histórico.
//
// O ano sai da data da consulta, porque é a ela que aqueles números se
// referem. Sem data, não dá para saber de que ano é a posição, e inventar
// um ano poria o dado na linha errada do comparativo.
export function historicoDoFormatoAntigo(atleta) {
  const a = atleta || {};
  if (!a.rankingEstadual && !a.rankingNacional) return [];
  const ano = Number(String(a.rankingAtualizadoEm || "").slice(0, 4));
  if (!Number.isFinite(ano) || ano < 1900) return [];
  return [{
    ano,
    estadual: String(a.rankingEstadual || "").trim(),
    nacional: String(a.rankingNacional || "").trim(),
  }];
}

// O histórico do atleta, venha ele do campo novo ou do formato antigo.
export function historicoDoAtleta(atleta) {
  const a = atleta || {};
  const proprio = historicoOrdenado(a.rankings);
  if (proprio.length) return proprio;
  return historicoOrdenado(historicoDoFormatoAntigo(a));
}
